import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { resolveRouteModule } from '../utils/permissionMaps';
import { Spin, Result, Button } from 'antd';
import { ThunderboltOutlined, LockOutlined } from '@ant-design/icons';

const ProtectedRoute = ({ children, permission, requireModule, requireSuperAdmin = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const { hasPermission, isModuleActive, isSuperAdmin } = usePermissions();
  const location = useLocation();

  if (loading) {
    const isAction = location.pathname.startsWith('/action');
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          {isAction ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8">
              <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-400 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/25">
                <ThunderboltOutlined className="text-white text-2xl" />
              </div>
              <Spin size="large" />
              <div
                className="mt-5 text-lg font-semibold text-gray-900 dark:text-white"
                style={{ fontFamily: 'CustomFont' }}
              >
                در حال ورود به ماژول اقدام...
              </div>
              <div
                className="mt-1 text-sm text-gray-500 dark:text-gray-400"
                style={{ fontFamily: 'CustomFont' }}
              >
                لطفاً چند لحظه صبر کنید
              </div>
            </div>
          ) : (
            <div className="text-lg">در حال بارگذاری...</div>
          )}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireSuperAdmin && Number(user?.is_superadmin) !== 1) {
    return (
      <AccessDeniedPage
        message="دسترسی فقط برای سوپرادمین"
        subTitle="برای مشاهده این بخش باید حساب شما سطح سوپرادمین داشته باشد"
      />
    );
  }

  if (isSuperAdmin) {
    return children;
  }

  if (location.pathname.startsWith('/action')) {
    return children;
  }

  const moduleKey = requireModule ?? resolveRouteModule(location.pathname);
  if (moduleKey && !isModuleActive(moduleKey)) {
    return (
      <AccessDeniedPage
        message="این ماژول برای شما فعال نیست"
        subTitle="برای دسترسی به این بخش، با مدیر سیستم هماهنگ کنید"
      />
    );
  }

  if (permission && !hasPermission(permission)) {
    return (
      <AccessDeniedPage
        message="دسترسی محدود شده"
        subTitle="شما مجوز دسترسی به این بخش را ندارید"
      />
    );
  }

  return children;
};

const AccessDeniedPage = ({ message, subTitle }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
    <Result
      icon={
        <div className="w-24 h-24 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
          <LockOutlined className="text-red-500 dark:text-red-400" style={{ fontSize: '48px' }} />
        </div>
      }
      title={
        <span className="text-2xl font-bold text-gray-800 dark:text-white" style={{ fontFamily: 'CustomFont' }}>
          {message}
        </span>
      }
      subTitle={
        <span className="text-gray-600 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
          {subTitle}
        </span>
      }
      extra={
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button type="primary" size="large" onClick={() => window.history.back()} style={{ fontFamily: 'CustomFont' }}>
            بازگشت
          </Button>
          <Button size="large" onClick={() => (window.location.href = '/dashboard')} style={{ fontFamily: 'CustomFont' }}>
            رفتن به داشبورد
          </Button>
        </div>
      }
    />
  </div>
);

export default ProtectedRoute;
